import Button from '@app-studio-web/ui/components/Button'
import Layout from '@app-studio-web/ui/components/Layout'
import QuestionPage from '@app-studio-web/ui/pages/QuestionPage'
import useUpdateMode from '@hooks/useUpdateMode'
import { saveAnswer } from '@utils/onboardingStorage'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, useSearchParams, useSubmit } from 'react-router-dom'

const QUESTION_KEY = 'letgo'

const OPTIONS = [
  {
    value: 'Sentimental value',
    translationKey: 'pages.onboarding.letgo.options.sentimental',
  },
  {
    value: 'Fear of regret',
    translationKey: 'pages.onboarding.letgo.options.fear',
  },
  {
    value: 'Anxiety about waste',
    translationKey: 'pages.onboarding.letgo.options.anxiety',
  },
  {
    value: 'It was a gift',
    translationKey: 'pages.onboarding.letgo.options.gift',
  },
  {
    value: 'Just feels wrong',
    translationKey: 'pages.onboarding.letgo.options.wrong',
  },
]

export default function LetGoPage() {
  const submit = useSubmit()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [previousAnswer, setPreviousAnswer] = useState<string | null>(null)
  const [sliderValue, setSliderValue] = useState(1000)
  const value = searchParams.get('value')
  const preselectedValue =
    value && OPTIONS.some((option) => option.value === value)
      ? value
      : undefined
  const { isUpdate, postToReactNative } = useUpdateMode()

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    // Get the answer value from the form
    const formData = new FormData(event.currentTarget)
    const value = formData.get('answer') as string

    if (!value) return

    const answer = {
      question_key: QUESTION_KEY,
      question: 'Which of these makes it hardest to let go?',
      answer_key: value,
      answer: value,
    }

    if (isUpdate) {
      postToReactNative(answer)
    } else {
      saveAnswer(QUESTION_KEY, answer)
      submit(event.currentTarget)
    }
  }

  // Transform options to the format expected by QuestionPage
  const buttons = OPTIONS.map((option) => ({
    text: t(option.translationKey),
    value: option.value,
  }))

  useEffect(() => {
    try {
      const onboardingAnswers = localStorage.getItem('onboarding_answers')
      if (onboardingAnswers) {
        const parsed = JSON.parse(onboardingAnswers)
        setPreviousAnswer(parsed.obstacle?.answer_key)
      }
    } catch (error) {
      console.error('Error parsing onboarding answers:', error)
    }
  }, [])

  if (previousAnswer !== 'Emotional attachment') {
    return (
      <Layout>
        <Form
          className="flex h-full flex-col"
          method="post"
          onSubmit={handleSubmit}
        >
          <div className="mt-8">
            <h1 className="custom-heading text-center text-3xl whitespace-pre-line">
              {t('pages.onboarding.letgo.heading2')}
            </h1>
          </div>

          <div className="flex flex-1 flex-col items-center justify-center gap-8 px-4">
            {/* add custom slider here */}

            <div className="relative w-full max-w-[500px]">
              <label htmlFor="labels-range-input" className="sr-only">
                Labels range
              </label>
              <input
                id="labels-range-input"
                type="range"
                value={sliderValue}
                min="100"
                max="1500"
                onChange={(e) => setSliderValue(Number(e.target.value))}
                className="slider-black-thumb h-2 w-full cursor-pointer appearance-none rounded-lg bg-[#6A6A6A4D]"
              />
              <div className="mt-2 flex justify-between">
                <span className="max-w-[80px] text-center font-bold">
                  {t('pages.onboarding.letgo.sliderLabels.notConfident')}
                  <br />
                  {t('pages.onboarding.letgo.sliderLabels.notConfidentLine2')}
                </span>
                <span className="max-w-[80px] text-center font-bold">
                  {t('pages.onboarding.letgo.sliderLabels.veryConfident')}
                  <br />
                  {t('pages.onboarding.letgo.sliderLabels.veryConfidentLine2')}
                </span>
              </div>
            </div>
          </div>

          <div className="mx-auto mt-auto mb-8 flex w-full max-w-[500px] flex-col gap-4">
            <Button
              title={t('shared.continue')}
              type="submit"
              dataTestId="button-continue-input"
              // disabled={!inputValue.trim()}
            />
          </div>
        </Form>
      </Layout>
    )
  }

  return (
    <QuestionPage
      heading={t('pages.onboarding.letgo.heading')}
      buttons={buttons}
      onSubmit={handleSubmit}
      headingSize="text-3xl text-center whitespace-pre-line"
      buttonTextSize="text-xl"
      buttonBgColor="bg-white"
      preselectedValue={preselectedValue}
    />
  )
}
